/**
 * <s-option-app-guard> — keeps the add-to-cart button of option-app products
 * (YMQ Product Options) locked until the app has rendered its options.
 *
 * Configurable products have a base price of 0 and get their selections,
 * required fields and surcharges from the app. Buying them without the app's
 * options would add an unconfigured 0 € item, so the server renders the
 * submit button natively disabled and this component only releases it once
 * app markup (class or id containing "ymq") appears inside the scope. The
 * app's own validation then handles required fields on submit.
 *
 * Attributes:
 *   data-scope    — CSS selector of the ancestor to watch (default: the element itself).
 *   data-timeout  — ms to wait before showing the fallback note (default: 10000).
 *
 * Required markup inside the element:
 *   button[data-option-submit]  — natively disabled submit button.
 *   [data-option-status]        — loading text, hidden once options render.
 *   [data-option-fallback]      — hidden note shown if options never render.
 */
class SOptionAppGuard extends HTMLElement {
  #observer;
  #timer;
  #released = false;

  connectedCallback() {
    const scopeSelector = this.dataset.scope;
    const scope = (scopeSelector && this.closest(scopeSelector)) || this;

    if (this.#hasAppMarkup(scope)) {
      this.#release();
      return;
    }

    this.#observer = new MutationObserver(() => {
      if (this.#hasAppMarkup(scope)) this.#release();
    });
    this.#observer.observe(scope, { childList: true, subtree: true });

    const timeout = Number.parseInt(this.dataset.timeout || '10000', 10);
    this.#timer = window.setTimeout(() => this.#showFallback(), timeout);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    window.clearTimeout(this.#timer);
  }

  #hasAppMarkup(scope) {
    return Boolean(scope.querySelector('[class*="ymq"]:not(script):not(style), [id*="ymq"]:not(script):not(style)'));
  }

  #release() {
    if (this.#released) return;
    this.#released = true;
    this.#observer?.disconnect();
    window.clearTimeout(this.#timer);

    const button = this.querySelector('button[data-option-submit]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }

    const status = this.querySelector('[data-option-status]');
    if (status instanceof HTMLElement) status.hidden = true;

    const fallback = this.querySelector('[data-option-fallback]');
    if (fallback instanceof HTMLElement) fallback.hidden = true;
  }

  #showFallback() {
    if (this.#released) return;

    const status = this.querySelector('[data-option-status]');
    if (status instanceof HTMLElement) status.hidden = true;

    const fallback = this.querySelector('[data-option-fallback]');
    if (fallback instanceof HTMLElement) fallback.hidden = false;
  }
}

if (!customElements.get('s-option-app-guard')) {
  customElements.define('s-option-app-guard', SOptionAppGuard);
}
